import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import HomeReviews from '../components/HomeReviews';
import {
  Truck,
  Plane,
  Ship,
  Package,
  Clock,
  Shield,
  Globe,
  Headphones,
  ArrowRight,
  Send,
  CheckCircle,
  Briefcase,
} from 'lucide-react';

const HERO_IMG = '/images/hero/jet-cargo-hero.png';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: ctxUser } = useAuth();
  const [user, setUser] = useState(null);
  const [trackInput, setTrackInput] = useState('');

  useEffect(() => {
    if (location.state?.user?.isLoggedIn) {
      setUser(location.state.user);
    } else if (ctxUser?.isLoggedIn) {
      setUser(ctxUser);
    }
  }, [location.state, ctxUser]);

  const handleTrackStrip = (e) => {
    e.preventDefault();
    const q = trackInput.trim();
    if (!q) return;
    navigate(`/tracking?p=${encodeURIComponent(q.toUpperCase())}`);
  };

  const services = [
    {
      icon: <Plane className="w-7 h-7" />,
      tone: 'blue',
      title: 'Air Freight',
      description: 'Express air cargo with global coverage and priority handling for time-sensitive shipments.',
      href: '/services',
    },
    {
      icon: <Ship className="w-7 h-7" />,
      tone: 'blue',
      title: 'Sea Freight',
      description: 'Cost-effective FCL and LCL ocean freight for full containers and consolidated loads.',
      href: '/services',
    },
    {
      icon: <Truck className="w-7 h-7" />,
      tone: 'green',
      title: 'Land Transport',
      description: 'Reliable trucking and last-mile delivery connecting ports, airports, and your door.',
      href: '/services',
    },
    {
      icon: <Package className="w-7 h-7" />,
      tone: 'orange',
      title: 'Packaging Solutions',
      description: 'Secure packing, crating, and labeling so your cargo arrives in perfect condition.',
      href: '/services',
    },
  ];

  const features = [
    {
      icon: <Globe className="w-5 h-5" />,
      title: 'Global Network',
      text: 'Partners and hubs worldwide for seamless multimodal logistics.',
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: 'Safe & Secure',
      text: 'Handled with care, compliance, and visibility at every step.',
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: 'Timely Delivery',
      text: 'Schedules you can trust — air, sea, or land, optimized for your lane.',
    },
    {
      icon: <Headphones className="w-5 h-5" />,
      title: '24/7 Support',
      text: 'Dedicated team ready to answer questions and resolve issues anytime.',
    },
  ];

  const heroStats = [
    { icon: <Shield className="w-6 h-6" strokeWidth={1.75} />, value: '100+', label: 'Countries' },
    { icon: <Briefcase className="w-6 h-6" strokeWidth={1.75} />, value: '10K+', label: 'Shipments Delivered' },
    { icon: <Headphones className="w-6 h-6" strokeWidth={1.75} />, value: '24/7', label: 'Customer Support' },
  ];

  return (
    <>
      <section className="jet-hero">
        <div
          className="jet-hero__bg"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          role="img"
          aria-label="Cargo ship, aircraft, and truck at port"
        />
        <div className="jet-hero__overlay" />
        <div className="jet-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Delivering Possibilities. Connecting <span className="jet-hero__accent">the World.</span>
          </motion.h1>
          <motion.p
            className="jet-hero__sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Fast, reliable and secure cargo solutions by Air, Sea or Land. We deliver your trust, across the globe.
          </motion.p>
          {user && (
            <motion.div
              className="jet-hero-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <p>Welcome back, {user.loginId}</p>
            </motion.div>
          )}
          <motion.div
            className="jet-hero__ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <Link to="/tracking" className="jet-btn-hero-primary">
              <Package className="w-5 h-5" />
              Track Your Shipment
            </Link>
            <Link to="/services" className="jet-btn-hero-outline">
              <Plane className="w-5 h-5" />
              Our Services
            </Link>
          </motion.div>
          <motion.div
            className="jet-hero-stats"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28 }}
          >
            {heroStats.map((s) => (
              <div key={s.label} className="jet-hero-stat">
                <div className="jet-hero-stat__icon">{s.icon}</div>
                <div className="jet-hero-stat__text">
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="jet-track-strip">
        <div className="jet-track-strip__inner">
          <div>
            <div className="jet-track-strip__label">
              <Package className="w-5 h-5" aria-hidden />
              Track Your Shipment
            </div>
            <form className="jet-track-form" onSubmit={handleTrackStrip}>
              <input
                type="text"
                placeholder="Enter Tracking Number"
                value={trackInput}
                onChange={(e) => setTrackInput(e.target.value)}
                aria-label="Tracking number"
              />
              <button type="submit">
                <Send className="w-4 h-4" />
                Track Now
              </button>
            </form>
          </div>
          <div className="jet-timeline" aria-hidden>
            <div className="jet-timeline__step jet-timeline__step--done">
              <div className="jet-timeline__icon-wrap">
                <Package className="w-5 h-5 jet-timeline__icon" />
              </div>
              <div>
                <strong>Picked Up</strong>
                <span style={{ display: 'block', color: '#94a3b8' }}>May 25, 2024</span>
              </div>
            </div>
            <span className="jet-timeline__arrow">→</span>
            <div className="jet-timeline__step jet-timeline__step--active">
              <div className="jet-timeline__icon-wrap">
                <Plane className="w-5 h-5 jet-timeline__icon" />
              </div>
              <div>
                <strong>In Transit</strong>
                <span style={{ display: 'block', color: '#94a3b8' }}>May 27, 2024</span>
              </div>
            </div>
            <span className="jet-timeline__arrow">→</span>
            <div className="jet-timeline__step jet-timeline__step--done">
              <div className="jet-timeline__icon-wrap">
                <CheckCircle className="w-5 h-5 jet-timeline__icon" />
              </div>
              <div>
                <strong>Delivered</strong>
                <span style={{ display: 'block', color: '#94a3b8' }}>May 30, 2024</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="jet-section jet-section--muted">
        <div className="jet-container">
          <h2 className="jet-section__title">Our Services</h2>
          <div className="jet-service-grid">
            {services.map((s) => (
              <motion.div
                key={s.title}
                className="jet-service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
              >
                <div className={`jet-service-card__icon jet-service-card__icon--${s.tone}`}>{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
                <Link to={s.href} className="jet-link-arrow">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="jet-section">
        <div className="jet-container">
          <div className="jet-split">
            <div>
              <h2 className="jet-why-title">Why Choose Jet Cargo?</h2>
              <div className="jet-features-row">
                {features.map((f) => (
                  <div key={f.title} className="jet-feature-mini">
                    <div className="jet-feature-mini__icon">{f.icon}</div>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="jet-quote-card">
              <span className="jet-quote-deco" aria-hidden>📦</span>
              <h3>Get a Quick Quote</h3>
              <p>
                Tell us about your lane, volume, and timeline — we will respond with options tailored to your budget
                and delivery goals.
              </p>
              <Link to="/contact" className="jet-btn-quote">
                Get Quote Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <HomeReviews />
    </>
  );
};

export default Home;
